import React, { useEffect, useState } from 'react';
import { IconButton, Dialog, Box, Typography, Grid } from '@mui/material/';
import { red } from '@mui/material/colors';
import CancelIcon from '@mui/icons-material/Cancel';
import Fade from '@mui/material/Fade';
import { Backdrop, CircularProgress } from '@mui/material';
import styled from '../../../utils/styled';

const PREFIX = 'Modal';

const classes = {
	paper: `${PREFIX}-paper`
};
const StyledCircularProgress = styled(CircularProgress)(({ theme }) => ({
	circle: {
		strokeLinecap: 'round' // Make the line ends rounded
	}
}));

const StyledDialog = styled(Dialog)(({ theme, $anchorOrigin, $margin }) => ({
	background: 'transparent',

	[`& .MuiPaper-root`]: {
		paddingTop: 'env(safe-area-inset-top)',
		paddingBottom: 'env(safe-area-inset-bottom)',

		margin: $margin === 'dense' ? 0 : theme.spacing(3),
		position: 'fixed',
		transform: 'none',
		// Vertical positioning
		...($anchorOrigin?.vertical === 'bottom' && { bottom: 0 }),
		...($anchorOrigin?.vertical === 'top' && { top: 0 }),
		...($anchorOrigin?.vertical === 'center' && {
			top: '50%'
		}),

		// Horizontal positioning
		...($anchorOrigin?.horizontal === 'left' && { left: 0 }),
		...($anchorOrigin?.horizontal === 'right' && { right: 0 }),
		...($anchorOrigin?.horizontal === 'center' && {
			left: '50%'
		}),

		// Combine translateX and translateY for "center" alignment
		...($anchorOrigin?.horizontal === 'center' &&
			$anchorOrigin?.vertical === 'center' && {
				transform: 'translate(-50%, -50%)' // Center-center
			}),
		...($anchorOrigin?.horizontal === 'center' &&
			$anchorOrigin?.vertical === 'top' && {
				transform: 'translateX(-50%)' // Center horizontally, top vertically
			}),
		...($anchorOrigin?.horizontal === 'center' &&
			$anchorOrigin?.vertical === 'bottom' && {
				transform: 'translateX(-50%)' // Center horizontally, bottom vertically
			})
	},

	[`& .${classes.paper}`]: {
		height: '100%',
		minHeight: '80%'
	}
}));

const Transition = React.forwardRef((props, ref) => <Fade ref={ref} {...props} />);

export default function BasicModal(props) {
	const [progress, setProgress] = useState(100); // Percentage progress (0-100)
	const [remainingTime, setRemainingTime] = useState(props.duration / 1000); // Remaining seconds

	const handleClose = () => {
		props.setOpenModal(false);
		if (props.setFormData) {
			props.setFormData(null);
		}

		const storage = localStorage.getItem('userData');
		if (storage) {
			const userData = JSON.parse(storage);

			if (userData.isFirstLogin) {
				userData.isFirstLogin = false;
				localStorage.setItem('userData', JSON.stringify(userData));
			}
		}
	};

	useEffect(() => {
		const startTime = Date.now();
		const timer = setInterval(() => {
			const elapsed = Date.now() - startTime;
			const remaining = Math.max(0, props.duration - elapsed);
			const progressValue = (remaining / props.duration) * 100;

			setProgress(progressValue);
			setRemainingTime(Math.ceil(remaining / 1000)); // Update remaining seconds

			if (remaining === 0) {
				clearInterval(timer);
				props.setOpenModal(false);
			}
		}, 100); // Update every 100ms for smooth animation

		return () => clearInterval(timer);
	}, [props.duration]);

	return (
		<StyledDialog
			slots={{ backdrop: Backdrop }}
			slotProps={{
				backdrop: {
					sx: {
						backgroundColor: props.backdropColor ?? 'rgba(69, 89, 167, 0.4)'
					}
				}
			}}
			$margin={props.margin}
			maxWidth={props.maxWidth ? props.maxWidth : 'lg'}
			open={props.openModal}
			onClose={handleClose}
			TransitionComponent={Transition}
			fullScreen={props.fullScreen ? props.fullScreen : false}
			$anchorOrigin={props.anchorOrigin || { horizontal: 'center', vertical: 'center' }} // Default anchorOrigin
			fullWidth
		>
			<Box sx={{ height: '100%' }} padding={3}>
				<Box display="flex" justifyContent="space-between">
					<Grid item xs={12} display="flex" alignItems="center">
						{props.duration && (
							<Box position="relative" display="inline-flex">
								<StyledCircularProgress
									variant="determinate"
									value={progress}
									size={50}
									thickness={4}
									color="primary"
								/>
								<Box
									top={0}
									left={0}
									bottom={0}
									right={0}
									position="absolute"
									display="flex"
									alignItems="center"
									justifyContent="center"
								>
									<Typography
										variant="caption"
										component="div"
										fontWeight="bold"
										color="textSecondary"
									>
										{remainingTime}s
									</Typography>
								</Box>
							</Box>
						)}
						<Box ml={2}>
							<Typography variant="h6" component="div" gutterBottom>
								{props.title}
							</Typography>
							<Typography variant="caption" component="div" gutterBottom>
								{props.subtitle}
							</Typography>
						</Box>
					</Grid>
					<Box display="flex" justifyContent="space-between" sx={{ height: 'fit-content' }}>
						<IconButton
							edge="start"
							color="inherit"
							size="medium"
							onClick={handleClose}
							aria-label="close"
						>
							<CancelIcon sx={{ color: red[400] }} />
						</IconButton>
					</Box>
				</Box>
				{props.children}
			</Box>
		</StyledDialog>
	);
}
